import React, {RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {tableColumns} from '../styles.js'

export interface LayoutColumn {
    id: string
    /** Stored or caller width, before fitting */
    width: number
    flex: number
    minWidth: number
}

/**
 * Widths of the columns filling the available width. Columns get or give up the difference in proportion to their
 * flex, but never go below their minWidth. Columns narrower than fixedColumnWidth (icon columns) or without flex keep
 * their width.
 */
export function fitWidths(columns: LayoutColumn[], available: number): Record<string, number> {
    const widths: Record<string, number> = {}

    columns.forEach((column) => {
        widths[column.id] = column.width
    })

    if (!(available > 0)) {
        return widths
    }

    let flexible = columns.filter((column) => column.flex > 0 && column.width >= tableColumns.fixedWidth)
    let free = available - columns.reduce((sum, column) => flexible.includes(column) ? sum : sum + column.width, 0)

    // Columns that would go below their minimum get it, the others share the rest
    for (;;) {
        const baseWidth = flexible.reduce((sum, column) => sum + column.width, 0)
        const totalFlex = flexible.reduce((sum, column) => sum + column.flex, 0)
        const extra = free - baseWidth

        const tooNarrow = flexible.filter((column) => column.width + extra * column.flex / totalFlex < column.minWidth)

        if (!tooNarrow.length) {
            break
        }

        tooNarrow.forEach((column) => {
            widths[column.id] = column.minWidth
            free -= column.minWidth
        })

        flexible = flexible.filter((column) => !tooNarrow.includes(column))
    }

    if (!flexible.length) {
        return widths
    }

    const baseWidth = flexible.reduce((sum, column) => sum + column.width, 0)
    const totalFlex = flexible.reduce((sum, column) => sum + column.flex, 0)
    const extra = free - baseWidth

    // Whole pixels, the last flexible column gets what rounding left over
    let left = free

    flexible.forEach((column, index) => {
        const width = index === flexible.length - 1 ? left : Math.floor(column.width + extra * column.flex / totalFlex)

        widths[column.id] = width
        left -= width
    })

    return widths
}

interface Resize {
    id: string
    nextId: string
    startX: number
    // Fitted widths when the drag started
    widths: Record<string, number>
}

/**
 * Fits the visible columns to the width of the container (followed with a ResizeObserver) and resizes them by
 * dragging: the border between a column and the next one moves, the other columns keep their width. While dragging
 * the widths are only state, onResizeEnd gets the widths of all visible columns to store.
 */
export default function useColumnLayout(containerRef: RefObject<HTMLElement>, columns: LayoutColumn[], onResizeEnd: (widths: Record<string, number>) => void) {
    const [containerWidth, setContainerWidth] = useState(0)
    const [containerHeight, setContainerHeight] = useState(0)
    const [dragWidths, setDragWidths] = useState<Record<string, number> | null>(null)

    // Measured before the first paint, so the columns don't jump
    useLayoutEffect(() => {
        const element = containerRef.current

        if (!element) {
            return
        }

        const measure = () => {
            setContainerWidth(element.offsetWidth)
            setContainerHeight(element.offsetHeight)
        }

        measure()

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure)

            return () => window.removeEventListener('resize', measure)
        }

        const observer = new ResizeObserver(measure)
        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    const available = containerWidth - tableColumns.fitOffsetCheckbox - tableColumns.fitOffsetScrollbar

    const fitted = useMemo(() => fitWidths(columns, available), [columns, available])

    const widths = dragWidths ? {...fitted, ...dragWidths} : fitted

    const latest = useRef({columns, fitted, onResizeEnd})
    latest.current = {columns, fitted, onResizeEnd}

    const resizeRef = useRef<Resize | null>(null)
    const listenersRef = useRef<{move: (event: MouseEvent) => void, up: (event: MouseEvent) => void} | null>(null)

    const removeListeners = () => {
        if (listenersRef.current) {
            window.removeEventListener('mousemove', listenersRef.current.move)
            window.removeEventListener('mouseup', listenersRef.current.up)
            listenersRef.current = null
        }
    }

    useEffect(() => removeListeners, [])

    // The widths of the column and the next one with the border moved by deltaX, both at least their minimum
    const dragged = (resize: Resize, deltaX: number) => {
        const {columns} = latest.current
        const column = columns.find((column) => column.id === resize.id)
        const next = columns.find((column) => column.id === resize.nextId)

        if (!column || !next) {
            return null
        }

        const total = resize.widths[resize.id] + resize.widths[resize.nextId]
        const width = Math.round(Math.max(column.minWidth, Math.min(resize.widths[resize.id] + deltaX, total - next.minWidth)))

        return {...resize.widths, [resize.id]: width, [resize.nextId]: total - width}
    }

    const startResize = useCallback((id: string, event: React.MouseEvent) => {
        const {columns, fitted} = latest.current
        const index = columns.findIndex((column) => column.id === id)

        if (index < 0 || index >= columns.length - 1) {
            return
        }

        event.preventDefault()
        removeListeners()

        const resize: Resize = {id, nextId: columns[index + 1].id, startX: event.clientX, widths: fitted}
        resizeRef.current = resize

        const move = (event: MouseEvent) => {
            const widths = dragged(resize, event.clientX - resize.startX)

            if (widths) {
                setDragWidths(widths)
            }
        }

        const up = (event: MouseEvent) => {
            removeListeners()
            resizeRef.current = null

            const widths = dragged(resize, event.clientX - resize.startX)

            // Stored first, so the columns don't go back to the old widths for a render
            if (widths) {
                latest.current.onResizeEnd(widths)
            }

            setDragWidths(null)
        }

        listenersRef.current = {move, up}
        window.addEventListener('mousemove', move)
        window.addEventListener('mouseup', up)

        setDragWidths(resize.widths)
    }, [])

    return {widths, startResize, resizing: !!dragWidths, containerHeight}
}
